// Vancouver-timezone calendar helpers for the Classes page.
// All "dates" are plain YYYY-MM-DD strings; math is done in UTC to avoid DST bugs.

export const VAN_TZ = "America/Vancouver";
export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function todayVan() {
  return new Intl.DateTimeFormat("en-CA", { timeZone: VAN_TZ }).format(new Date());
}

const toUTC = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return Date.UTC(y, m - 1, d);
};
const fromUTC = (ms) => new Date(ms).toISOString().slice(0, 10);

export function addDays(dateStr, n) {
  return fromUTC(toUTC(dateStr) + n * 86400000);
}

export function weekStart(dateStr) {
  const day = new Date(toUTC(dateStr)).getUTCDay(); // 0=Sun
  return addDays(dateStr, -((day + 6) % 7)); // back to Monday
}

export function weekDates(startStr) {
  return Array.from({ length: 7 }, (_, i) => addDays(startStr, i));
}

export function weekdayOf(dateStr) {
  return WEEKDAYS[(new Date(toUTC(dateStr)).getUTCDay() + 6) % 7];
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export function fmtShort(dateStr) {
  const [, m, d] = dateStr.split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}`;
}
export function fmtRange(startStr) {
  const end = addDays(startStr, 6);
  const [y] = end.split("-");
  return `${fmtShort(startStr)} – ${fmtShort(end)}, ${y}`;
}
export function fmtLong(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  return `${weekdayOf(dateStr)}, ${MONTHS[m - 1]} ${d}, ${y}`;
}

export function toMinutes(t) {
  if (!t) return null;
  const [h, m] = t.split(":").map(Number);
  return h * 60 + (m || 0);
}
export function fmtTime(t) {
  const mins = toMinutes(t);
  if (mins === null) return "";
  const h24 = Math.floor(mins / 60);
  const m = mins % 60;
  const ampm = h24 >= 12 ? "PM" : "AM";
  const h = h24 % 12 || 12;
  return `${h}:${String(m).padStart(2, "0")} ${ampm}`;
}

const norm3 = (w) => (w || "").slice(0, 3).toLowerCase();

/** Expand CRM schedules into dated occurrences for the given week (display only).
 *  Respects start_date/end_date; schedules without weekdays appear on start_date only. */
export function occurrencesForWeek(schedules, dates) {
  const out = [];
  for (const s of schedules) {
    const wds = (s.weekdays || []).map(norm3);
    for (const date of dates) {
      if (s.start_date && date < s.start_date) continue;
      if (s.end_date && date > s.end_date) continue;
      const matches = wds.length
        ? wds.includes(norm3(weekdayOf(date)))
        : s.start_date === date;
      if (matches) out.push({ key: `${s.id}_${date}`, date, schedule: s });
    }
  }
  return out.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      (toMinutes(a.schedule.start_time) || 0) - (toMinutes(b.schedule.start_time) || 0)
  );
}

/** Next display occurrence date for a schedule on/after fromDate (display only). */
export function nextOccurrenceDate(s, fromDate) {
  const wds = (s.weekdays || []).map(norm3);
  if (!wds.length) {
    return s.start_date && s.start_date >= fromDate ? s.start_date : null;
  }
  let d = fromDate;
  if (s.start_date && s.start_date > d) d = s.start_date;
  const end = s.end_date || addDays(d, 370);
  while (d <= end) {
    if (wds.includes(norm3(weekdayOf(d)))) return d;
    d = addDays(d, 1);
  }
  return null;
}

/** Bounded forward search: first week (Monday) on/after fromDate containing an
 *  upcoming occurrence from the given schedules. Returns null if none in horizon. */
export function findNextAvailableWeek(schedules, fromDate, dayFilter = "all", maxWeeks = 52) {
  if (!schedules.length) return null;
  let wk = weekStart(fromDate);
  for (let i = 0; i < maxWeeks; i++) {
    const occs = occurrencesForWeek(schedules, weekDates(wk)).filter(
      (o) => o.date >= fromDate && (dayFilter === "all" || weekdayOf(o.date) === dayFilter)
    );
    if (occs.length) return wk;
    wk = addDays(wk, 7);
  }
  return null;
}

// Program colour coding (never relied on alone — titles/badges always shown)
const COLOR_RULES = [
  { re: /brick/i, c: { hex: "#6DBE45", bg: "bg-emerald-50", border: "border-emerald-500", text: "text-emerald-900" } },
  { re: /galileo/i, c: { hex: "#EF4444", bg: "bg-rose-50", border: "border-rose-500", text: "text-rose-900" } },
  { re: /algoplay/i, c: { hex: "#00A6B2", bg: "bg-cyan-50", border: "border-cyan-500", text: "text-cyan-900" } },
  { re: /robo|robot|coding/i, c: { hex: "#123B6D", bg: "bg-blue-50", border: "border-blue-600", text: "text-blue-900" } },
  { re: /big\s*builder/i, c: { hex: "#F59E0B", bg: "bg-amber-50", border: "border-amber-500", text: "text-amber-900" } },
  { re: /3d|model/i, c: { hex: "#8B5CF6", bg: "bg-purple-50", border: "border-purple-500", text: "text-purple-900" } },
  { re: /camp/i, c: { hex: "#FF8C42", bg: "bg-orange-50", border: "border-orange-500", text: "text-orange-900" } },
  { re: /tennis|sport|movement/i, c: { hex: "#10B981", bg: "bg-teal-50", border: "border-teal-500", text: "text-teal-900" } },
];
const DEFAULT_COLOR = { hex: "#123B6D", bg: "bg-slate-50", border: "border-slate-400", text: "text-slate-900" };

export function programColor(schedule) {
  const hay = `${schedule.title || ""} ${schedule.schedule_type || ""}`;
  for (const r of COLOR_RULES) if (r.re.test(hay)) return r.c;
  return DEFAULT_COLOR;
}

export function isBookingClosed(s) {
  return s.booking_enabled === false || s.booking_closed === true;
}
export function isFull(s) {
  return (s.seats_available ?? 0) <= 0;
}

/** Age filter: does schedule.age_range ("7-12") overlap the bucket? */
export function ageMatches(ageRange, bucket) {
  if (bucket === "all") return true;
  if (!ageRange) return true;
  const nums = String(ageRange).match(/\d+/g);
  if (!nums) return true;
  const lo = Number(nums[0]);
  const hi = Number(nums[1] ?? nums[0]);
  const [blo, bhi] = bucket.split("-").map(Number);
  return lo <= bhi && hi >= blo;
}
